import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { BOOKING_URL } from '../../data/site'

const concerns = [
  {
    name: 'Neck & Back Pain',
    learnMore: '/conditions/neck-back-pain',
    treatments: [
      {
        service: 'Massage Therapy',
        description:
          'Reduce muscle tension, improve mobility, and relieve everyday aches and stiffness.',
      },
      {
        service: 'Athletic Therapy',
        description:
          'Assess contributing movement patterns and combine hands-on care with targeted exercise to reduce pain and restore function through the neck and back.',
      },
    ],
  },
  {
    name: 'Headaches & Jaw Tension',
    learnMore: '/conditions/tmj-headaches',
    treatments: [
      {
        service: 'Massage Therapy',
        description:
          'Relieve tension through the neck, jaw, and surrounding muscles that may contribute to headaches and discomfort.',
      },
      {
        service: 'Acupuncture',
        description:
          'Support pain relief, nervous system regulation, and tension reduction through targeted treatment.',
      },
    ],
  },
  {
    name: 'Stress, Burnout & Nervous System Care',
    treatments: [
      {
        service: 'Massage Therapy',
        description:
          'Calm tension, support recovery, and help your body shift out of stress mode.',
      },
      {
        service: 'Acupuncture',
        description:
          'Support sleep, stress regulation, and nervous system balance through gentle, targeted treatment.',
      },
      {
        service: 'Naturopathic Medicine',
        description:
          'Address stress, fatigue, sleep, and recovery through a whole-body approach to wellness.',
      },
    ],
  },
  {
    name: 'Pregnancy & Postpartum Care',
    learnMore: '/conditions/pregnancy-postpartum',
    treatments: [
      {
        service: 'Massage Therapy',
        description:
          'Support changing muscles and joints while helping reduce tension and discomfort throughout pregnancy and postpartum recovery.',
      },
      {
        service: 'Naturopathic Medicine',
        description:
          'Support energy, recovery, hormones, and overall wellness during pregnancy and postpartum.',
      },
    ],
  },
  {
    name: 'Hormonal Health',
    treatments: [
      {
        service: 'Naturopathic Medicine',
        description:
          'Support hormonal balance and concerns like PMS, PCOS, fertility, thyroid, perimenopause, and menstrual health through a root-cause, whole-body approach.',
      },
    ],
  },
  {
    name: 'Chronic Tension & Mobility',
    learnMore: '/conditions/desk-worker-pain',
    treatments: [
      {
        service: 'Massage Therapy',
        description:
          'Relieve persistent tightness and help your body move with more ease day to day.',
      },
      {
        service: 'Athletic Therapy',
        description:
          'Combine manual therapy with mobility and strengthening work to ease persistent tightness and help you move more freely.',
      },
    ],
  },
  {
    name: 'Injury Recovery',
    learnMore: '/conditions',
    treatments: [
      {
        service: 'Athletic Therapy',
        description:
          'Prevent, assess, and treat musculoskeletal injuries with hands-on techniques and therapeutic exercise so you can recover confidently and return to activity.',
      },
    ],
  },
  {
    name: 'Athletic Performance',
    learnMore: '/services/athletic-therapy',
    treatments: [
      {
        service: 'Athletic Therapy',
        description:
          'Support training, strength, mobility, and return to sport so you can perform at your best and stay in the activities you love.',
      },
    ],
  },
]

const serviceSlugs = {
  'Massage Therapy': 'massage-therapy',
  'Athletic Therapy': 'athletic-therapy',
  Acupuncture: 'acupuncture',
  'Naturopathic Medicine': 'naturopathic-medicine',
}

const Specialties = () => {
  const [activeIndex, setActiveIndex] = useState(null)

  const handleToggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index)
  }

  return (
    <section className="bg-cream-200 py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
          {/* Left column — copy */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <span className="font-script text-brand-orange-500 italic text-lg mb-3 block">
              Our Services
            </span>
            <h2 className="font-display text-dark-700 text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.15] mb-6">
              Integrated Care That Supports{' '}
              <span className="underline decoration-brand-orange-500/40 underline-offset-4">
                How Your Body Moves, Feels, and Recovers
              </span>
            </h2>
            <p className="font-sans text-dark-500 text-base md:text-lg leading-relaxed mb-4">
            Care is tailored to your body and goals. Explore some of the common concerns we support and the approaches we may use to help.
            </p>
          </motion.div>

          {/* Right column — service pills + expandable details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
          >
            <span className="font-sans text-dark-500 text-xs tracking-[0.2em] uppercase block mb-5">
              Areas of Expertise:
            </span>

            <div className="flex flex-wrap gap-3 mb-4">
              {concerns.map((concern, index) => (
                <motion.button
                  key={concern.name}
                  onClick={() => handleToggle(index)}
                  className={`inline-block border rounded-full px-5 py-2.5 font-sans text-sm transition-all duration-200 cursor-pointer
                    ${
                      activeIndex === index
                        ? 'bg-brand-orange-500 text-white border-brand-orange-500'
                        : 'border-dark-300 text-dark-700 hover:bg-brand-orange-500 hover:text-white hover:border-brand-orange-500'
                    }`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.4,
                    delay: 0.3,
                    ease: 'easeOut',
                  }}
                >
                  {concern.name}
                </motion.button>
              ))}
            </div>

            {/* Expandable detail panel */}
            <AnimatePresence mode="wait">
              {activeIndex !== null && (
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="bg-cream-300/50 rounded-2xl p-6 mb-6">
                    <h3 className="font-display text-dark-700 text-lg font-medium mb-4 uppercase tracking-wide">
                      {concerns[activeIndex].name}
                    </h3>
                    <div className="space-y-5">
                      {concerns[activeIndex].treatments.map((treatment) => (
                        <div
                          key={treatment.service}
                          className="border-t border-dark-300/30 pt-4 first:border-t-0 first:pt-0"
                        >
                          <h4 className="font-display text-dark-700 text-base font-medium mb-1">
                            <Link
                              to={`/services/${serviceSlugs[treatment.service]}`}
                              className="hover:text-brand-orange-500 transition-colors"
                            >
                              {treatment.service}
                            </Link>
                          </h4>
                          <p className="font-sans text-dark-500 text-base leading-relaxed mb-3">
                            {treatment.description}
                          </p>
                          <a
                            href={BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-brand-green-600 text-white rounded-full px-5 py-2 font-sans text-xs tracking-[0.1em] uppercase
                                       hover:bg-brand-green-700 transition-colors duration-200"
                          >
                            Book {treatment.service}
                          </a>
                        </div>
                      ))}
                    </div>
                    {concerns[activeIndex].learnMore && (
                      <Link
                        to={concerns[activeIndex].learnMore}
                        className="inline-block mt-5 font-sans text-sm text-brand-orange-600 hover:text-brand-orange-500 underline underline-offset-4"
                      >
                        Learn more about {concerns[activeIndex].name.toLowerCase()} →
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-dark-700 rounded-full px-6 py-3 font-sans text-sm tracking-[0.15em] uppercase text-dark-700
                         hover:bg-dark-700 hover:text-white transition-all duration-200"
            >
              Explore Our Services
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Specialties
